import React, { useEffect, useRef, useState } from "react";
import styles from "./selectComponent.module.scss";
import arrow from "../../../assets/images/pages/login/select_arrow.svg";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";

interface SelectOption {
  value: string | number;
  label: string;
}

interface SelectComponentProps {
  label?: string;
  name: string;
  options: SelectOption[];
  value?: string | number;
  selectedValue?: string | number;
  placeholder?: string;
  onChange: (value: string | number) => void;
  className?: string;
  icon?: string;
  error?: string;
  disabled?: boolean;
  selectedPackagesCount?: number;
  customStyles?: {};
  customTextStyles?: {};
}

const SelectComponent: React.FC<SelectComponentProps> = ({
  label,
  name,
  options,
  value,
  selectedValue,
  placeholder = "",
  onChange,
  className = "",
  icon,
  error,
  disabled = false,
  selectedPackagesCount = 0,
  customStyles,
  customTextStyles,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localSelectedValue, setLocalSelectedValue] = useState<
    string | number | undefined
  >(selectedValue);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { data } = useSelector((state: RootState) => state.translationData);

  useEffect(() => {
    setLocalSelectedValue(selectedValue);
  }, [selectedValue]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] =
    useState<SelectOption[]>(options);

  useEffect(() => {
    setFilteredOptions(
      options.filter(
        (option) =>
          option.label &&
          option.label.toLowerCase().includes(searchTerm?.toLowerCase() || "")
      )
    );
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (optionValue: string | number) => {
    setLocalSelectedValue(optionValue);
    setIsOpen(false);
    onChange(optionValue);
  };

  const handleInputFocus = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  return (
    <div style={{ width: "100%" }}>
      <div
        className={`${styles.select_container} ${styles[className]}`}
        ref={dropdownRef}
      >
        {label && <p className={styles.select_text}>{label}</p>}
        <div
          className={styles.custom_select}
          onClick={() => {
            if (!disabled) {
              setIsOpen((prev) => !prev);
              if (
                ["seller_id", "category_id"].includes(name) &&
                searchInputRef.current
              ) {
                searchInputRef.current.focus();
              }
            }
          }}
          style={{
            borderColor: isOpen ? "#97bea5" : "#d1d1d1",
            cursor: disabled ? "not-allowed" : "pointer",
            backgroundColor: disabled ? "#f5f5f5" : "white",
            ...(customStyles || {}),
          }}
          aria-disabled={disabled}
        >
          <div className={styles.label_icon_container}>
            {icon && <img src={icon} alt="icon" className={styles.icon} />}

            {["seller_id", "category_id"].includes(name) ? (
              <input
                ref={searchInputRef}
                type="text"
                className={styles.search_input}
                placeholder={
                  !localSelectedValue && searchTerm === "" ? placeholder : ""
                }
                value={
                  localSelectedValue
                    ? options.find((opt) => opt.value === localSelectedValue)
                        ?.label || ""
                    : searchTerm
                }
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setIsOpen(true);
                  if (localSelectedValue) {
                    setLocalSelectedValue(undefined);
                    onChange("");
                  }
                }}
                onFocus={handleInputFocus}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(true);
                }}
              />
            ) : (
              <div className={styles.selected_option}>
                <p className={styles.placeholder} style={customTextStyles}>
                  {selectedPackagesCount > 0
                    ? `${selectedPackagesCount} ${data?.auth_common_package_selected}`
                    : options.find(
                        (option) => option.value === localSelectedValue
                      )?.label ||
                      (localSelectedValue === undefined ? placeholder : "")}
                </p>
              </div>
            )}
          </div>
          <div className={styles.arrow}>
            <img width={"13px"} src={arrow} alt="arrow" />
          </div>
        </div>

        {isOpen && !disabled && (
          <div className={styles.dropdown_wrapper}>
            <ul className={styles.options_list}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <li
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    className={`${styles.option} ${
                      option.value === localSelectedValue ? styles.selected : ""
                    }`}
                  >
                    {option.label}
                  </li>
                ))
              ) : (
                <li className={styles.no_results}>Nəticə tapılmadı </li>
              )}
            </ul>
          </div>
        )}
      </div>
      <p className={styles.error_message}>{error}</p>
    </div>
  );
};

export default SelectComponent;
